import { useState } from 'react';
import { Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react';
import { ModalPanel, Button, Input } from '@/contasis';
import { useAuth } from '@/contexts/ContextoAuth';
import { useToast } from '@/contexts/ContextoToast';
import { usuariosService } from '@/services/api/usuarios.service';
import { useVentanasIniciales } from '@/hooks/useVentanasIniciales';

function validarPasswordNueva(password: string): string | null {
  if (password.length < 8) return 'La contraseña nueva debe tener al menos 8 caracteres';
  if (!/[A-Z]/.test(password)) return 'La contraseña nueva debe tener al menos una letra mayúscula';
  if (!/[a-z]/.test(password)) return 'La contraseña nueva debe tener al menos una letra minúscula';
  if (!/[0-9]/.test(password)) return 'La contraseña nueva debe tener al menos un número';
  if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) return 'La contraseña nueva debe tener al menos un símbolo especial';
  return null;
}

export const ModalPrimerAccesoApiKey = () => {
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useToast();

  const [mostrarFormPassword, setMostrarFormPassword] = useState(false);
  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNuevo, setPasswordNuevo] = useState('');
  const [passwordConfirmar, setPasswordConfirmar] = useState('');
  const [mostrarActual, setMostrarActual] = useState(false);
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [procesando, setProcesando] = useState(false);

  const { mostrarPassword } = useVentanasIniciales();

  const debeMostrarse = mostrarPassword;

  if (!debeMostrarse || !user) return null;

  const handleContinuar = async () => {
    setProcesando(true);
    try {
      await usuariosService.marcarRequiereCambioPassword(user.id, false);
      updateUser({ requiereCambioPassword: false });
    } catch {
      showError('No se pudo guardar tu elección. Inténtalo de nuevo.');
    } finally {
      setProcesando(false);
    }
  };

  const handleCambiarPassword = async () => {
    if (!passwordActual || !passwordNuevo || !passwordConfirmar) {
      showError('Completa todos los campos');
      return;
    }
    if (passwordNuevo !== passwordConfirmar) {
      showError('Las contraseñas nuevas no coinciden');
      return;
    }
    const errorValidacion = validarPasswordNueva(passwordNuevo);
    if (errorValidacion) {
      showError(errorValidacion);
      return;
    }

    setProcesando(true);
    try {
      const res = await usuariosService.cambiarPassword(user.id, { passwordActual, passwordNuevo });
      if (res.ok) {
        showSuccess('Contraseña actualizada correctamente');
        updateUser({ requiereCambioPassword: false });
      } else {
        showError('La contraseña actual es incorrecta');
      }
    } catch {
      showError('Ocurrió un error al cambiar la contraseña');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <ModalPanel
      isOpen
      title="Bienvenido a Senciyo"
      subtitle="Tu cuenta se creó con una contraseña generada automáticamente"
      size="medium"
      onClose={() => {}}
      closeOnClickOutside={false}
      closeOnEsc={false}
      disableClose
      footerActions={
        mostrarFormPassword ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => setMostrarFormPassword(false)} disabled={procesando}>
              Volver
            </Button>
            <Button variant="primary" size="sm" loading={procesando} onClick={() => void handleCambiarPassword()}>
              Actualizar contraseña
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={() => void handleContinuar()} loading={procesando}>
              Continuar con mi contraseña actual
            </Button>
            <Button variant="primary" size="sm" onClick={() => setMostrarFormPassword(true)} disabled={procesando}>
              Cambiar contraseña
            </Button>
          </>
        )
      }
    >
      {!mostrarFormPassword ? (
        <div className="px-6 py-8 flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-brand" />
          </div>
          <div className="space-y-1.5 max-w-sm">
            <h3 className="font-display text-h5 text-primary">¿Deseas cambiar tu contraseña?</h3>
            <p className="text-ui-sm text-secondary">
              Puedes seguir usando la contraseña generada al crear tu cuenta o establecer una nueva ahora.
            </p>
          </div>
        </div>
      ) : (
        <div className="px-6 py-6 flex flex-col gap-4">
          <div className="flex gap-3 items-center">
            <div className="w-9 h-9 rounded-lg bg-surface-1 border border-default flex items-center justify-center text-tertiary shrink-0">
              <KeyRound size={18} />
            </div>
            <p className="text-ui-sm text-tertiary">Ingresa tu contraseña actual (la generada al crear tu cuenta) y elige una nueva.</p>
          </div>

          <Input
            label="Contraseña actual"
            type={mostrarActual ? 'text' : 'password'}
            value={passwordActual}
            onChange={e => setPasswordActual(e.target.value)}
            rightIcon={
              <button type="button" onClick={() => setMostrarActual(v => !v)} className="text-tertiary hover:text-secondary transition-colors">
                {mostrarActual ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <Input
            label="Nueva contraseña"
            type={mostrarNuevo ? 'text' : 'password'}
            value={passwordNuevo}
            onChange={e => setPasswordNuevo(e.target.value)}
            rightIcon={
              <button type="button" onClick={() => setMostrarNuevo(v => !v)} className="text-tertiary hover:text-secondary transition-colors">
                {mostrarNuevo ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <div>
            <Input
              label="Confirmar nueva contraseña"
              type={mostrarConfirmar ? 'text' : 'password'}
              value={passwordConfirmar}
              onChange={e => setPasswordConfirmar(e.target.value)}
              rightIcon={
                <button type="button" onClick={() => setMostrarConfirmar(v => !v)} className="text-tertiary hover:text-secondary transition-colors">
                  {mostrarConfirmar ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />
            {passwordConfirmar.length > 0 && (
              <p className={`text-caption mt-1 ${passwordConfirmar === passwordNuevo ? 'text-success' : 'text-error'}`}>
                {passwordConfirmar === passwordNuevo ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
              </p>
            )}
          </div>
        </div>
      )}
    </ModalPanel>
  );
};
